<template>
    <div>
    <!-- DIALOGS -->
        <SelectionRequiredDialog />
        <DeletionDialog context="SelectedMappings" />	
        <ContextHelpDialog />
    <!-- LIST PAGE COMPONENT -->
        <BaseListPage
            headline="lang_ACCOUNT_MAPPINGS"
            :context="context"
            :alvqa="alvqa"
            searchCols="2"
            :stickyHeader="true"
        >
            <template v-slot:filter>
                <v-col>
                    <DropdownFilter 
                        :context="context + 'LanguagesFilter'"
                        label="lang_GENERIC_CAPTION_LANGUAGE"
                        :items="mappingsLanguagesOptions"
                        :alvqa="alvqa + '-list-dropdown-mappings-languages-filter'"
                    />
                </v-col>
                <v-col>
                    <DropdownFilter 
                        :context="context + 'DirectionsFilter'"
                        label="lang_ACCOUNT_MAPPINGS_CAPTION_DIRECTIONS"
                        :items="mappingsDirectionsOptions"
                        :alvqa="alvqa + '-list-dropdown-mappings-directions-filter'"
                        :clearable="false"
                    />
                </v-col>
                <v-col cols="2">
                    <DropdownFilter 
                        :context="context + 'DomainsFilter'"
                        label="lang_ACCOUNT_MAPPINGS_CAPTION_DOMAINS"
                        itemText="text"
                        :items="mappingsDomainsOptions"
                        :alvqa="alvqa + '-list-dropdown-mappings-domains-filter'"
                        :clearable="false"
                    />
                </v-col>
                <v-col v-if="hasDomainContextHelp" cols="auto" class="d-flex align-center pl-0">
                    <ContextHelpIcon 
                        :context="mappingHelpContext"
                        :alvqa="alvqa + '-list-domain-context-help-icon'"
                    />
                </v-col>
                <v-col cols="3">
                    <DropdownFilter 
                        :context="context + 'MaintainersFilter'"
                        label="lang_ACCOUNT_MAPPINGS_CAPTION_MAINTAINERS"
                        itemText="text"
                        :items="mappingsMaintainersOptions"
                        :alvqa="alvqa + '-list-dropdown-mappings-maintainers-filter'"
                        :clearable="false"
                    />
                </v-col>
			</template>

        <!-- DATA TABLE COMPONENT--> 
            <template v-slot:dataTable>
                <BaseDataTableList
                    :headers="headers"
                    :context="context"
                    :alvqa="alvqa"
                    :stickyTopSlot="true"
                >
                <!-- TOPSLOT: actions, imports etc. -->
                    <template v-slot:topSlot><MappingsTopSlot /></template>
                    
                <!-- DATA TABLE CONTENT: columns -->
                    <template v-slot:body="{ item }">
                        <ExpandIcon 
                            :isExpanded="item.isExpanded" 
                            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-icon-expand'"
                        /> 

                        <td class="ellipsis">
                            <BaseTooltip 
                                :simpleText="item.item.title" 
                                :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-mappings-title'"
                            />
                        </td>

                        <td class="ellipsis">
                            <BaseTooltip 
                                :simpleText="item.item.abbreviation" 
                                :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-mappings-abbreviation'"
                            />
                        </td>

                        <td class="ellipsis">
                            <BaseTooltip
                                :simpleText="item.item.language ? item.item.language.toUpperCase() : ''" 
                                :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-mappings-language'"
                            />
                        </td>


                        <td class="ellipsis">
                            <BaseTooltip 
                                :simpleText="getSourceName(item.item)" 
                                :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-mappings-source'"
                            />
                        </td>

                        <td class="no_ellipsis"
                            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-mappings-direction'">
                            {{ getDirection(item.item.direction) }}
                        </td>
                        <td class="no_ellipsis"
                            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-mappings-domain'">
                            {{ getDomain(item.item.domain) }}
                        </td>
                    </template>

                    <!-- EXPAND: Read Only View -->
                    <template v-slot:expandSlot="{ id }">
                        <MappingsReadOnly :itemId='id' />
                    </template>
                </BaseDataTableList>
            </template>
        </BaseListPage>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

import BaseDataTableList from '@/components/_base/BaseDataTableList'
import BaseListPage from '@/components/_base/BaseListPage'
import BaseTooltip from '@/components/_base/BaseTooltip'

import MappingsTopSlot from './MappingsTopSlot'
import MappingsReadOnly from './_readonly/MappingsReadOnly'
import ExpandIcon from '@/components/_buttons/ExpandIcon'
import DropdownFilter from '@/components/_filters/DropdownFilter'

import SelectionRequiredDialog from '@/components/_dialogs/SelectionRequiredDialog'
import DeletionDialog from '@/components/_dialogs/DeletionDialog'
import ContextHelpDialog from '@/components/_dialogs/ContextHelpDialog'
import ContextHelpIcon from '@/components/_buttons/ContextHelpIcon'
import { getMappingHelpContext } from './mappingHelpContext'

import 
    { 
        DropdownAccountMappingsDirectionsOptions,
        DropdownAccountMappingsDomainsOptions,
    } 
from '@/assets/dropdowns/alvDropdowns.js'

export default {
    name: "MappingsList",
    components: {
        BaseDataTableList,
        BaseListPage,
        BaseTooltip,

        MappingsTopSlot,
        MappingsReadOnly,
        ExpandIcon,
        DropdownFilter,

        SelectionRequiredDialog,
        DeletionDialog,
        ContextHelpDialog,
        ContextHelpIcon,
    },
    data() {
        return {
            context: "Mappings",
            alvqa: 'account-mappings',
            hasDomainContextHelp: false
        }
    },
    computed: {
        ...mapGetters(['getAccountMappingsSearch']),

        mappingHelpContext() {
            return getMappingHelpContext(
                this.$store.getters.getSelectedMappingsDirectionsFilter,
                this.$store.getters.getSelectedMappingsDomainsFilter
            )
        },
        
        mappingsDirectionsOptions() {
            return DropdownAccountMappingsDirectionsOptions
        },
        mappingsDomainsOptions() {
            return DropdownAccountMappingsDomainsOptions
        },
        mappingsLanguagesOptions() {
            let languages = JSON.parse(JSON.stringify(this.$store.getters.getGeneralLanguages))
            return languages
        },
        mappingsMaintainersOptions() {
            // Filter for self made mappings is always present
            let retArr = [
                {
                    text: 'lang_ACCOUNT_MAPPINGS_CAPTION_FILTER_OPTION_MAINTAINER_SELF', 
                    value: 'self',
                    alvqa: 'account-mappings-maintainer-self' 
                },
                {
                    text: 'lang_ACCOUNT_MAPPINGS_CAPTION_FILTER_OPTION_MAINTAINER_ALL', 
                    value: 'all',
                    alvqa: 'account-mappings-maintainer-all' 
                }
            ]
            
            // Get users list that is fetched via API on this component load
            let users = this.$store.getters.getUsersWithShareable
            users.forEach((element, index) => {
                users[index] = {
                    text: `${element.first_name} ${element.last_name} (${element.company_name})`,
                    value: element.id,
                    alvqa: 'account-mappings-maintainer-id-' + element.id,
                }
            })
            retArr.push(...users)

            return retArr
        },
        
        headers() {
			return [
				{
					text: '', 
					value: 'data-table-select', 
                    width: "60px",
                    sortable: false,
                    class: 'text-no-wrap'
				},
				{
					text: '', 
					value: 'data-table-expand',
					align: 'center', 
                    width: "48px",
                    sortable: false,
                    class: 'text-no-wrap'
				},
                {
                    text: this.$t('lang_ACCOUNT_MAPPINGS_CAPTION_TITLE'), 
                    value: 'title', 
                    align: 'left', 
                    sortable: false
                }, 			
                {
                    text: this.$t('lang_GENERIC_CAPTION_ABBREVIATION'), 
                    value: 'abbreviation', 
                    align: 'left', 
                    sortable: false
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_LANGUAGE'), 
                    value: 'language', 
                    align: 'left', 
                    sortable: false,
                    width: '80px'
                }, 			
                {
                    text: this.$t('lang_GENERIC_CAPTION_SOURCE'), 
                    value: 'source', 
                    align: 'left', 
                    sortable: false,
                    width: '80px'
                }, 			
                {
                    text: this.$t('lang_ACCOUNT_MAPPINGS_CAPTION_DIRECTIONS'), 
                    value: 'direction', 
                    align: 'left', 
                    sortable: false,
                    width: '80px'
                },
                {
                    text: this.$t('lang_ACCOUNT_MAPPINGS_CAPTION_DOMAINS'), 
                    value: 'domain', 
                    align: 'left', 
                    sortable: false,
                    class: 'text-no-wrap'
                },
				{
					text: '', 
					value: 'editForm',
					align: 'right',
					sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
				}
			] 
		}        
    },
    methods: {
        getSourceName(item) { // contains ALVID of A-Z Society or Distributor
            let AzList = (item.direction == "import") ? this.$store.getters.getAzForMappingsImport : this.$store.getters.getAzForMappingsExport
            let match = AzList.find(type => { return type.value === item.source })
            if(match !== undefined) return match.text 
            else return item.sourceName
        },
        getDirection(directionValue) {
            let match = DropdownAccountMappingsDirectionsOptions.find(type => { return type.value === directionValue })
            if(match !== undefined) return this.$t(match.text) 
            else return ''
        },
        getDomain(domainValue) {
            let match = DropdownAccountMappingsDomainsOptions.find(type => { return type.value === domainValue })
            if(match !== undefined) return this.$t(match.text) 
            else return ''
        },
        resetFilters() {
            this.$store.commit('setSelectedMappingsMaintainersFilter', 'self')
            this.$store.commit('setSelectedMappingsDirectionsFilter', null)
            this.$store.commit('setSelectedMappingsDomainsFilter', null)

            // Preselect language filter to the current users language
            //this.$store.commit('setSelectedMappingsLanguagesFilter', this.$i18n.locale)
        }
    },
    watch: {
        mappingHelpContext: {
            immediate: true,
            async handler(ctx) {
                if (!ctx) {
                    this.hasDomainContextHelp = false
                    return
                }
                const available = await this.$alvPlugin.checkContextHelpAvailability(ctx)
                if (ctx === this.mappingHelpContext) {
                    this.hasDomainContextHelp = available
                }
            }
        }
    },
    created() {
        this.$store.dispatch("fetchAzForMappings");
        this.$store.dispatch("fetchUsersWithShareable");
        this.$store.commit('setMappings', [])
        
        // Only reset filters on first visit (when maintainer filter is still empty array)
        let currentMaintainer = this.$store.getters.getSelectedMappingsMaintainersFilter
        if (!currentMaintainer || (Array.isArray(currentMaintainer) && currentMaintainer.length === 0)) {
            this.resetFilters()
        }
    }
}
</script>